import type { FeaturePrototype } from '../prototype/FeaturePrototype';
import { ChamferCommandAdapter } from './chamfer/ChamferCommandAdapter';
import { ExtrudeCommandAdapter } from './extrude/ExtrudeCommandAdapter';
import { FilletCommandAdapter } from './fillet/FilletCommandAdapter';
import { RevolveCommandAdapter } from './revolve/RevolveCommandAdapter';
import { RibCommandAdapter } from './rib/RibCommandAdapter';
import { ShellCommandAdapter } from './shell/ShellCommandAdapter';
import type { FeatureCommandAdapter } from './FeatureCommandAdapter';

/**
 * 特征命令适配器注册表。
 */
export class FeatureCommandAdapterRegistry {
    /** 已注册适配器列表。 */
    private readonly adapters: FeatureCommandAdapter[] = [];

    /**
     * @param adapters 初始适配器列表。
     */
    public constructor(adapters: FeatureCommandAdapter[] = []) {
        this.registerMany(adapters);
    }

    /**
     * 注册适配器。
     *
     * @param adapter 适配器实例。
     * @returns 当前注册表，便于链式调用。
     */
    public register(adapter: FeatureCommandAdapter): this {
        this.adapters.push(adapter);
        return this;
    }

    /**
     * 批量注册适配器。
     *
     * @param adapters 适配器列表。
     * @returns 当前注册表，便于链式调用。
     */
    public registerMany(adapters: FeatureCommandAdapter[]): this {
        adapters.forEach((adapter) => {
            this.register(adapter);
        });
        return this;
    }

    /**
     * 解析负责指定命令的适配器。
     *
     * @param command 命令对象。
     * @returns 命中的适配器；不支持时返回 null。
     */
    public resolveAdapter(command: CadCommand): FeatureCommandAdapter | null {
        return this.adapters.find((adapter) => adapter.supports(command)) ?? null;
    }

    /**
     * 从命令恢复特征原型。
     *
     * @param command 命令对象。
     * @returns 特征原型；无适配器支持时返回 null。
     */
    public createPrototypeFromCommand(command: CadCommand): FeaturePrototype | null {
        for (const adapter of this.adapters) {
            if (!adapter.supports(command)) {
                continue;
            }
            const prototype = adapter.createPrototypeFromCommand(command);
            if (prototype) {
                return prototype;
            }
        }
        return null;
    }
}

/**
 * 全局特征命令适配器注册表实例。
 */
export const featureCommandAdapterRegistry = new FeatureCommandAdapterRegistry()
    .registerMany([
        new ChamferCommandAdapter(),
        new ExtrudeCommandAdapter(),
        new FilletCommandAdapter(),
        new RevolveCommandAdapter(),
        new RibCommandAdapter(),
        new ShellCommandAdapter(),
    ]);
